import TF from "@/components/forms/TextField.module.css";
import TC from "@/components/table/Table.module.css";

import { DeleteButton } from "@/components/button/DeleteButton.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import { formPropsToAriaProps } from "@/components/forms/ariaController.tsx";
import { RhfError } from "@/components/forms/FormFieldErrors.tsx";
import { requiredMessage } from "@/components/forms/requiredMessage.tsx";
import { WSelect } from "@/components/forms/WSelect.tsx";
import { WTable, WTableBody } from "@/components/table/WTable.tsx";
import { EditableStringList } from "@/modules/network/firewall/EditableList.tsx";
import { endPortLabel, startPortLabel } from "@/modules/network/firewall/firewallLabels.tsx";
import { ruleEndpointSpecs, type RuleType, ruleTypes } from "@/modules/network/firewall/ruleSpec.tsx";
import { validateEndPort, validatePort } from "@/modules/network/firewall/validatePorts.ts";
import type { FirewallForm } from "@/modules/network/NetworkCreate.tsx";
import { Trans, useLingui } from "@lingui/react/macro";
import type { FirewallRuleDirectionType } from "@warrenio/api-spec/spec.oats.gen";
import { Input, Label, NumberField } from "react-aria-components";
import { Controller, useFieldArray, useFormContext, useWatch } from "react-hook-form";

export function RulesList({
    direction,
    name,
}: {
    direction: FirewallRuleDirectionType;
    name: "inbound_rules" | "outbound_rules";
}) {
    const form = useFormContext<FirewallForm>();
    const { t } = useLingui();
    const { control, clearErrors } = form;
    const { fields } = useFieldArray({ control, name });
    const defaultType = ruleTypes[0];

    const typeValues = useWatch({
        control,
        name: `${name}`, // watching the entire array of items
    });

    function onAdd() {
        // XXX: `append` is broken for some reason
        form.setValue(name, [
            ...form.getValues(name),
            {
                type: defaultType,
                direction: direction,
                protocol: defaultType.protocol[0],
                port_start: defaultType.port_start,
                port_end: defaultType.port_end,
                endpoint_spec_type: "any",
                endpoint_spec: [],
            },
        ]);
    }

    function onDelete(index: number) {
        form.setValue(
            name,
            form.getValues(name).filter((_, i) => i !== index),
        );
    }

    function changeType(item: RuleType, index: number) {
        form.setValue(`${name}.${index}.protocol`, item.protocol[0]);
        form.clearErrors(`${name}.${index}.port_start`);
        form.clearErrors(`${name}.${index}.port_end`);
        form.clearErrors(`${name}.${index}.endpoint_spec`);
        form.setValue(`${name}.${index}.port_start`, item.port_start);
        form.setValue(`${name}.${index}.port_end`, item.port_end);
    }

    function changeEndpointSpecType(index: number) {
        form.setValue(`${name}.${index}.endpoint_spec`, []);
        form.clearErrors(`${name}.${index}.endpoint_spec`);
    }

    return (
        <WTable
            afterTable={
                <WButton
                    color="primary"
                    variant="dashed"
                    icon="jp-icon-add"
                    action={() => onAdd()}
                    className="w-full"
                    isDisabled={fields.length >= 64}
                >
                    <Trans>Add new rule</Trans>
                </WButton>
            }
        >
            <thead>
                <tr>
                    <th>
                        <Trans>Type</Trans>
                    </th>
                    <th>
                        <Trans>Protocol</Trans>
                    </th>
                    <th>
                        <Trans>Port range</Trans>
                    </th>
                    <th>{direction === "inbound" ? <Trans>Sources</Trans> : <Trans>Destinations</Trans>}</th>
                    <th />
                    <th />
                </tr>
            </thead>
            <WTableBody>
                {fields.map((item, index) => {
                    const currentType = typeValues?.[index]?.type; // This is the selected type object for this row
                    const protocolOptions = currentType.protocol;

                    return (
                        <tr key={item.id} className={TC.firewall}>
                            <td>
                                <Controller
                                    control={control}
                                    name={`${name}.${index}.type`}
                                    render={(f) => (
                                        <WSelect
                                            aria-label="Select type"
                                            items={ruleTypes}
                                            getKey={(item) => item.type}
                                            getTextValue={(item) => item.type}
                                            searchItems
                                            className="max-w-10rem"
                                            onChange={(item) => {
                                                f.field.onChange(item);
                                                changeType(item, index);
                                            }}
                                            value={f.field.value}
                                        >
                                            {(item) => item.title}
                                        </WSelect>
                                    )}
                                />
                            </td>
                            <td>
                                <Controller
                                    control={control}
                                    name={`${name}.${index}.protocol`}
                                    render={(f) => (
                                        <WSelect
                                            aria-label="Select protocol"
                                            items={protocolOptions}
                                            getKey={(item) => item}
                                            getTextValue={(item) => item}
                                            searchItems
                                            className="max-w-6rem"
                                            {...formPropsToAriaProps(f)}
                                            isDisabled={protocolOptions.length < 2}
                                        >
                                            {(item) => (item === "any" ? <Trans>Any</Trans> : item.toUpperCase())}
                                        </WSelect>
                                    )}
                                />
                            </td>
                            <td>
                                {form.getValues(`${name}.${index}.protocol`) === "any" ||
                                form.getValues(`${name}.${index}.protocol`) === "icmp" ||
                                form.getValues(`${name}.${index}.protocol`) === "icmp6" ? (
                                    <div className="pt-2">-</div>
                                ) : (
                                    <div className="flex gap-0.5em justify-between">
                                        <div className="w-4rem">
                                            <Controller
                                                control={control}
                                                name={`${name}.${index}.port_start`}
                                                rules={{
                                                    required: t(requiredMessage),
                                                    validate: (v) =>
                                                        currentType.type === "ICMP" ? undefined : validatePort(v),
                                                }}
                                                render={(f) => (
                                                    <NumberField
                                                        isReadOnly={!currentType.isCustom}
                                                        className={TF.NumberField}
                                                        isRequired
                                                        formatOptions={{
                                                            useGrouping: false,
                                                        }}
                                                        {...formPropsToAriaProps(f)}
                                                        value={f.field.value ?? undefined}
                                                    >
                                                        <Label className="hidden">{startPortLabel}</Label>
                                                        <div>
                                                            <Input className={TF.Input} />
                                                            <RhfError error={f.fieldState.error} />
                                                        </div>
                                                    </NumberField>
                                                )}
                                            />
                                        </div>

                                        {(currentType.port_end ?? currentType.isCustom) && (
                                            <>
                                                <div className="pt-2">-</div>
                                                <div className="w-4rem">
                                                    <Controller
                                                        control={control}
                                                        name={`${name}.${index}.port_end`}
                                                        rules={{
                                                            validate: (v, f) =>
                                                                validateEndPort(v, f[name][index].port_start),
                                                        }}
                                                        render={(f) => (
                                                            <NumberField
                                                                isReadOnly={!currentType.isCustom}
                                                                className={TF.NumberField}
                                                                formatOptions={{
                                                                    useGrouping: false,
                                                                }}
                                                                {...formPropsToAriaProps(f)}
                                                                value={f.field.value ?? undefined}
                                                            >
                                                                <Label className="hidden">{endPortLabel}</Label>
                                                                <div>
                                                                    <Input className={TF.Input} />
                                                                    <RhfError error={f.fieldState.error} />
                                                                </div>
                                                            </NumberField>
                                                        )}
                                                    />
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                            </td>
                            <td>
                                <Controller
                                    control={control}
                                    name={`${name}.${index}.endpoint_spec_type`}
                                    render={(f) => (
                                        <WSelect
                                            aria-label="Select spec"
                                            items={ruleEndpointSpecs}
                                            getKey={(item) => item.type}
                                            getTextValue={(item) => item.type}
                                            searchItems
                                            className="max-w-8rem"
                                            onChange={(item) => {
                                                f.field.onChange(item.type);
                                                changeEndpointSpecType(index);
                                            }}
                                            valueKey={f.field.value}
                                        >
                                            {(item) => item.title}
                                        </WSelect>
                                    )}
                                />
                            </td>
                            <td>
                                <Controller
                                    control={control}
                                    name={`${name}.${index}.endpoint_spec`}
                                    rules={{
                                        required:
                                            form.watch(`${name}.${index}.endpoint_spec_type`) === "any"
                                                ? false
                                                : t(requiredMessage),
                                    }}
                                    render={({ field, fieldState: { error } }) => (
                                        <EditableStringList
                                            isDisabled={form.watch(`${name}.${index}.endpoint_spec_type`) === "any"}
                                            items={field.value ?? undefined}
                                            onChange={(value) => form.setValue(`${name}.${index}.endpoint_spec`, value)}
                                            onInputChange={() => {
                                                // Clear the errors when the user starts doing something (they will be re-validated on submit)
                                                clearErrors(`${name}.${index}.endpoint_spec`);
                                            }}
                                            // Pass a ref so we can focus the input when submit is pressed and the field is invalid
                                            inputRef={field.ref}
                                            errorMessage={error?.message}
                                        />
                                    )}
                                />
                            </td>
                            <td className="text-right">
                                <div className="pt-2.5">
                                    <DeleteButton inTable action={() => onDelete(index)} />
                                </div>
                            </td>
                        </tr>
                    );
                })}
            </WTableBody>
        </WTable>
    );
}
